import { useMemo } from "react";
import { useActiveCycleWithPhases } from "@src/packages/cycles/hooks/useCycles";
import { useRetrievalClinicInstructions } from "@src/packages/cycles/hooks/useRetrievalClinicInstructions";
import type { StepPerspective } from "@src/packages/homepage/sections/treatmentFlows";
import { useTranslations } from "@src/packages/i18n";
import { toLocalDateString } from "@src/packages/utils/date";
import {
  getFrozenSpecimenSelectionForCycle,
  isFrozenOocyteCycle,
} from "../cryovault/frozenCycleSelection";
import { useSpecimens } from "../cryovault/useSpecimens";
import { resolveCurrentStepExplainerContext } from "./cycleStepExplainer";
import {
  buildStepExplainer,
  resolveStepExplainerId,
  type StepExplainer,
} from "./stepExplainers";

/**
 * The explainer for the step of her open cycle she is on, in full, or null
 * when no step is under way. Read the same way as the treatment hub's tab for
 * that step (`resolveCurrentStepExplainerContext`), including her clinic's own
 * retrieval instructions.
 */
export function useCurrentStepExplainer(
  perspective: StepPerspective,
): StepExplainer | null {
  const t = useTranslations();
  const { cycle, phases } = useActiveCycleWithPhases();
  // Only an IVF or ICSI cycle can run on frozen eggs, and only the specimen
  // records can say so; the hub reads the same records.
  const mayUseFrozenEggs =
    cycle?.cycleType === "ivf" || cycle?.cycleType === "icsi";
  const { data: specimens } = useSpecimens(undefined, {
    enabled: mayUseFrozenEggs,
  });
  const retrievalClinicInstructions = useRetrievalClinicInstructions(phases);
  // The current step can change with the date, so the day is part of the key.
  const todayIso = toLocalDateString(new Date());

  return useMemo(() => {
    const usingFrozenOocytes =
      mayUseFrozenEggs &&
      isFrozenOocyteCycle(
        cycle,
        getFrozenSpecimenSelectionForCycle(specimens, cycle?.id ?? null),
        specimens,
      );
    const context = resolveCurrentStepExplainerContext({
      t,
      cycle,
      phases,
      perspective,
      usingFrozenOocytes,
      // Only the day is read; midday keeps it the same local day.
      now: new Date(`${todayIso}T12:00:00`),
    });
    const id = context ? resolveStepExplainerId(context) : null;
    return context && id
      ? buildStepExplainer(t, id, { ...context, retrievalClinicInstructions })
      : null;
  }, [
    cycle,
    mayUseFrozenEggs,
    perspective,
    phases,
    retrievalClinicInstructions,
    specimens,
    t,
    todayIso,
  ]);
}
